import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@genuin/ui/lib/utils";

// TODO: think about composite theme for icon as well.
const buttonVariants = cva(
  "gencl:inline-flex gencl:items-center gencl:px-4 gencl:text-body-0-semi-bold gencl:justify-center gencl:hover:cursor-pointer gencl:gap-2 gencl:whitespace-nowrap gencl:rounded-lg gencl:transition-[color,box-shadow] gencl:disabled:pointer-events-none gencl:disabled:opacity-50 [&_svg]:gencl:pointer-events-none [&_svg:not([class*='size-'])]:gencl:size-4 gencl:shrink-0 [&_svg]:gencl:shrink-0 gencl:outline-none",
  {
    variants: {
      variant: {
        default: "",
        icon: "gencl:has-[>svg:only-child]:px-2 gencl:[&_svg]:shrink-0",
        rounded: "gencl:rounded-full",
      },
      theme: {
        primary:
          "gencl:bg-primary gencl:text-white! gencl:shadow-xs gencl:hover:bg-primary-600 gencl:[&_svg]:stroke-white",
        secondary:
          "gencl:bg-secondary-50 gencl:text-black! gencl:hover:bg-secondary-100",
        outline:
          "gencl:border gencl:border-secondary-150 gencl:hover:bg-secondary-50 gencl:bg-white",
        text: "gencl:text-primary",
        custom: "",
        navigation:
          "gencl:p-0 gencl:size-12! gencl:rounded-full! gencl:flex-center gencl:bg-secondary-800 gencl:text-white gencl:backdrop-blur-sm gencl:hover:bg-secondary-600 gencl:border-0 gencl:transition-all gencl:duration-200 [&_svg]:gencl:size-5",
        overlay: "",
      },
      size: {
        xs: "gencl:size-6",
        sm: "gencl:h-9 gencl:rounded-lg gencl:gap-1.5 gencl:text-body-1-semi-bold",
        md: "gencl:h-10",
        lg: "gencl:h-12",
      },
      shape: {
        default: "gencl:rounded-lg",
        pill: "gencl:rounded-full",
        square: "gencl:p-2.5",
        circle: "gencl:rounded-full gencl:p-1.5 gencl:aspect-square",
      },
    },
    compoundVariants: [
      {
        variant: "icon",
        size: "sm",
        class: "gencl:size-9",
      },
      {
        variant: "icon",
        size: "md",
        class: "gencl:size-10",
      },
      {
        variant: "icon",
        size: "lg",
        class: "gencl:size-12",
      },
      {
        shape: "pill",
        size: "sm",
        class: "gencl:text-body-2-semi-bold gencl:py-1 gencl:px-3 gencl:h-auto",
      },
      {
        shape: "pill",
        size: "md",
        class: "gencl:text-body-2-semi-bold gencl:py-1 gencl:px-4 gencl:h-auto",
      },
      {
        shape: "pill",
        size: "lg",
        class: "gencl:text-body-2-semi-bold gencl:py-2 gencl:px-5 gencl:h-auto",
      },
      {
        theme: "overlay",
        size: "xs",
        class:
          "gencl:size-6 gencl:[&_svg]:shrink-0 gencl:rounded-full gencl:p-1.5! gencl:bg-black/40 gencl:backdrop-blur-[1px]",
      },
      {
        theme: "overlay",
        size: "sm",
        class:
          "gencl:size-8 gencl:rounded-full gencl:p-0.5! gencl:bg-black/40 gencl:backdrop-blur-[1px]",
      },
      {
        theme: "overlay",
        shape: "circle",
        class: "gencl:p-1.5! gencl:size-8 gencl:bg-black/40",
      },
    ],
    defaultVariants: {
      size: "md",
      theme: "primary",
      variant: "default",
      shape: "default",
    },
  }
);

export type ButtonPropsType = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({
  className,
  theme,
  size,
  shape,
  variant,
  asChild = false,
  ...props
}: ButtonPropsType) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ theme, size, variant, shape }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants, type ButtonPropsType as ButtonProps };
